import {inject, Injectable, OnDestroy} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {distinctUntilChanged, map, Observable, shareReplay, Subject} from 'rxjs';
import {SessionStorageService} from './sessionstorage.service';
import {AuthOptions, OidcSecurityService} from 'angular-auth-oidc-client';
import {LOGOUT_CHANNEL, LOGOUT_MESSAGE_TYPE} from './logout-broadcast';

/** Метка логаута в sessionStorage вкладки-инициатора: когда нажали «Выход». */
const LOGOUT_MARKER_KEY = 'logout-started-at';

/** Сколько метка остаётся годной. Дольше — считаем её протухшей. */
const LOGOUT_MARKER_TTL_MS = 60_000;

@Injectable({providedIn: 'root'})
export class AuthService implements OnDestroy {
    private readonly oidc = inject(OidcSecurityService);
    private readonly sessionStorage = inject(SessionStorageService);

    /**
     * Единственный источник признака «вошёл» — поток библиотеки.
     * Никакой копии пользователя в localStorage/sessionStorage: токены лежат
     * в localStorage под управлением библиотеки, отдельного дубля нет.
     */
    readonly isAuthenticated$: Observable<boolean> = this.oidc.isAuthenticated$.pipe(
        map(({isAuthenticated}) => isAuthenticated),
        distinctUntilChanged(),
        shareReplay({bufferSize: 1, refCount: false}),
    );

    readonly isAuthenticated = toSignal(this.isAuthenticated$, {initialValue: false});

    /**
     * Один BroadcastChannel на вкладку — и отправка, и приём через него.
     * Отправитель по спецификации не получает собственное сообщение, поэтому
     * вкладка, нажавшая «Выход», не услышит себя и не перезагрузится, отменив
     * уже начатый переход на Keycloak.
     */
    private readonly logoutChannel = new BroadcastChannel(LOGOUT_CHANNEL);
    private readonly remoteLogout = new Subject<void>();

    /** «В другой вкладке вышли». Свои сообщения сюда не попадают. */
    readonly remoteLogout$ = this.remoteLogout.asObservable();

    constructor() {
        this.logoutChannel.addEventListener('message', (ev: MessageEvent) => {
            if (ev.data?.type === LOGOUT_MESSAGE_TYPE) {
                this.remoteLogout.next();
            }
        });
    }

    ngOnDestroy(): void {
        this.logoutChannel.close();
    }

    /**
     * Разлогин: уводим на end-session Keycloak, а остальные вкладки оповещаем
     * позже — из completeLogout() на бут после возврата.
     *
     * Порядок важен. Если крикнуть сразу, соседние вкладки перезагрузятся, пока
     * SSO-сессия Keycloak ещё жива: перезагрузившись без токена, они уйдут в
     * authorize и молча получат новый — «Выход» выглядел бы не сработавшим.
     * Метка нужна и для id_token_hint: logoff() читает его из хранилища, поэтому
     * чужие вкладки не должны стирать токены до завершения этого вызова.
     */
    logout(): void {
        this.sessionStorage.saveData(LOGOUT_MARKER_KEY, Date.now());
        this.oidc.logoff().subscribe({
            next: () => undefined,
            error: () => this.oidc.logoffLocal(),
        });
    }

    /**
     * Бут после возврата с end-session: сессия Keycloak уже уничтожена, можно
     * оповещать остальные вкладки — они перезагрузятся и уйдут на страницу входа,
     * а не вернутся в приложение.
     *
     * @returns true, если вкладка была инициатором логаута и сигнал отправлен.
     */
    completeLogout(): boolean {
        const startedAt = this.sessionStorage.getData<number>(LOGOUT_MARKER_KEY);
        if (!startedAt) return false;

        this.sessionStorage.removeData(LOGOUT_MARKER_KEY);
        // Метка из прошлой сессии вкладки: сигнал давно неактуален.
        if (Date.now() - startedAt > LOGOUT_MARKER_TTL_MS) return false;

        this.logoffLocal();
        this.broadcastLogout();
        return true;
    }

    /** Только локальная очистка токенов, без обращения к Keycloak. */
    logoffLocal(): void {
        this.oidc.logoffLocal();
    }

    /**
     * Оповестить остальные вкладки: в них тоже нужно сбросить состояние и перезагрузиться.
     *
     * Вкладки одного приложения — first-party, сигнал между ними доходит. А вот из
     * iframe, который открыл Keycloak, не доходит: браузер разделяет хранилище между
     * сайтом вкладки и чужим сайтом, внутри которого открыт iframe.
     */
    broadcastLogout(): void {
        this.logoutChannel.postMessage({type: LOGOUT_MESSAGE_TYPE});
    }

    /**
     * Уход на Keycloak с сохранением текущего адреса, чтобы вернуться в тот же раздел.
     * returnUrl хранится в sessionStorage: токены общие для вкладок, а адрес возврата — нет.
     */
    loginSso(returnUrl?: string): void {
        if (returnUrl) {
            this.sessionStorage.saveData('returnUrl', returnUrl);
        }
        const options: AuthOptions = {
            customParams: {kc_locale: 'ru'}
        };
        this.oidc.authorize(undefined, options);
    }

    getReturnUrl(): string | null {
        const returnUrl = this.sessionStorage.getData<string>('returnUrl');
        if (returnUrl) {
            this.sessionStorage.removeData('returnUrl');
        }
        return returnUrl ?? null;
    }

    getAccessToken(): Observable<string> {
        return this.oidc.getAccessToken();
    }

    /** Принудительное обновление сессии; при невозможности пробрасывает ошибку наверх. */
    forceRefreshSession(): Observable<unknown> {
        return this.oidc.forceRefreshSession();
    }
}
